import { Close, FilterNone, Fullscreen, Minimize } from '@mui/icons-material';
import { Box, IconButton, Slide, type SlideProps, Tooltip, Typography, useTheme } from '@mui/material';
import MuiDialog from '@mui/material/Dialog';
import Paper, { type PaperProps } from '@mui/material/Paper';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { EHelpId } from '@sharedInterfaces/Language/languageTexts/helpDialog';
import type { AppState } from '@store/store';
import React, { useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import { useSelector } from 'react-redux';
import { useDialog } from '../DialogManager';
import HelpDialogButton from '../HelpDialog/HelpDialog';

interface DialogProps {
    id: string;
    children: React.ReactNode;
    className?: string;
    footer?: React.ReactNode;
    title: string;
    bigWidth?: true;
    helpId?: EHelpId;
    style?: React.CSSProperties;
    onClose: () => void;
}

function Dialog(props: DialogProps) {
    const { minimizeDialog, maximizeDialog, restoreDialog, focusDialog, setDialogState } = useDialog();
    const dialogStates = useSelector((state: AppState) => state.dialogs.dialogs);
    const dialogState = useSelector((state: AppState) => state.dialogs.dialogs.find((d) => d.id === props.id));

    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).helpDialog;
    const modalBodyRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    //Active Dialog is with highest zIndex that is not minimized
    const isActiveDialog: boolean = !dialogStates.some(
        (dialog) => dialog.zIndex > (dialogState ? dialogState.zIndex : 0) && dialog.windowState !== 'minimized',
    );

    useEffect(() => {
        if (!dialogState) return;
        if (dialogState.title === props.title) {
            return;
        }
        setDialogState(props.id, { title: props.title });
    }, [dialogState]);

    const onMinimize = () => {
        minimizeDialog(props.id);
    };
    const onMaximize = () => {
        maximizeDialog(props.id);
    };
    const onRestore = () => {
        restoreDialog(props.id);
    };
    const onFocusDialog = () => {
        focusDialog(props.id);
    };
    const maxWidth = dialogState?.windowState === 'full' ? 'xl' : props.bigWidth ? 'xl' : 'lg';
    return (
        <MuiDialog
            open={dialogState?.windowState !== 'minimized'}
            maxWidth={maxWidth}
            className={[props.className, dialogState?.windowState === 'full' ? 'fullScreenDialog' : undefined]
                .filter((d) => d)
                .join(' ')}
            fullWidth={props.bigWidth || dialogState?.windowState === 'full'}
            hideBackdrop={true}
            // onClose={onMinimize}
            TransitionComponent={Transition}
            style={{
                zIndex: dialogState ? dialogState.zIndex : 1302,
                margin: 0,
                borderRadius: 0,
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                pointerEvents: 'none',
                // backgroundColor: 'var(--color-secondary)',
                // boxShadow: '0px 0px 0px 0px rgba(0,0,0,0.2)',
            }}
            container={() => document.getElementById('dialogContainer')}
            PaperComponent={dialogState?.windowState === 'full' ? Paper : DraggablePaper}
            PaperProps={{
                style: {
                    margin: 16,
                    boxShadow: theme.shadows[8],
                    borderRadius: 8,
                    border: 'none',
                    overflow: 'hidden',
                },
            }}
            slotProps={{
                backdrop: {
                    style: {
                        pointerEvents: 'none',
                    },
                },
            }}
            slots={{ backdrop: () => null }}
        >
            <Box
                onMouseDown={onFocusDialog}
                style={props.style}
                sx={{
                    display: 'flex',
                    pointerEvents: 'all',
                    flexDirection: 'column',
                    maxHeight: 'calc(100vh - 64px)',
                    borderRadius: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        minHeight: { xs: 56, sm: 64, md: 70 },
                        background: isActiveDialog
                            ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                            : theme.palette.action.disabled,
                        color: theme.palette.primary.contrastText,
                        px: { xs: 2, sm: 3 },
                        py: 1,
                        cursor: 'move',
                        borderRadius: '8px 8px 0 0',
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            flex: 1,
                            mr: 1,
                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: theme.palette.primary.contrastText,
                        }}
                    >
                        {props.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {dialogState && (
                            <>
                                <Tooltip title={langStrings.minimize} className="no-drag">
                                    <IconButton
                                        aria-label={langStrings.minimize}
                                        size="small"
                                        sx={{
                                            color: theme.palette.primary.contrastText,
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                                        }}
                                        onClick={onMinimize}
                                    >
                                        <Minimize fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {dialogState.windowState === 'normal' ? (
                                    <Tooltip title={langStrings.maximize} className="no-drag">
                                        <IconButton
                                            aria-label={langStrings.maximize}
                                            size="small"
                                            sx={{
                                                color: theme.palette.primary.contrastText,
                                                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                                            }}
                                            onClick={onMaximize}
                                        >
                                            <Fullscreen fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title={langStrings.restore} className="no-drag">
                                        <IconButton
                                            aria-label={langStrings.restore}
                                            size="small"
                                            sx={{
                                                color: theme.palette.primary.contrastText,
                                                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                                            }}
                                            onClick={onRestore}
                                        >
                                            <FilterNone fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </>
                        )}
                        <Tooltip title={langStrings.close} className="no-drag">
                            <IconButton
                                aria-label={langStrings.close}
                                size="small"
                                sx={{
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                                }}
                                onClick={props.onClose}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {props.helpId && (
                            <Box sx={{ ml: 1 }}>
                                <HelpDialogButton helpId={props.helpId} />
                            </Box>
                        )}
                    </Box>
                </Box>
                <Box
                    className="modal-body no-drag"
                    ref={modalBodyRef}
                    sx={{
                        flexGrow: 1,
                        height: '100%',
                        overflowY: 'auto',
                        p: { xs: 2, sm: 3 },
                        background:
                            'var(--background-gradient, linear-gradient(to bottom right, rgba(64, 61, 219, 0.05), rgba(236, 230, 240, 0.05)))',
                        position: 'relative',
                    }}
                >
                    {props.children}
                </Box>
                {props.footer && (
                    <>
                        <Box
                            sx={{
                                height: 1,
                                bgcolor: theme.palette.divider,
                                mx: 2,
                            }}
                        />
                        <Box
                            className="modal-footer"
                            sx={{
                                bgcolor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                                p: { xs: 1.5, sm: 2 },
                                cursor: 'move',
                                borderRadius: '0 0 8px 8px',
                                borderTop: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <Box className="no-drag" sx={{ cursor: 'default' }}>
                                {props.footer}
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        </MuiDialog>
    );
}

export default Dialog;

interface DraggablePaperProps extends PaperProps {
    // focusDialog?: () => void
}
function DraggablePaper(props: DraggablePaperProps) {
    return (
        <Draggable handle=".modal-header, .modal-footer" cancel={'.no-drag'}>
            <Paper {...props} />
        </Draggable>
    );
}

const Transition = React.forwardRef(function Transition(props: SlideProps, ref: React.Ref<unknown>) {
    return <Slide direction="up" ref={ref} {...props} />;
});
